import React, { useState, useEffect, useMemo } from 'react';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, CheckCircle, Info, Bot, Sparkles, Clock, 
  FileSpreadsheet, Printer, ArrowRight, Search, FileText,
  Package2, User, CreditCard, Lock, Calendar
} from 'lucide-react';
import ClientProductCard from '@/components/client-po/ClientProductCard';
import ClientLegalitasSection from '@/components/client-po/ClientLegalitasSection';
import ClientPOCheckout from '@/pages/ClientPOCheckout';
import ClientPOImportModal from '@/components/client-po/ClientPOImportModal';
import ClientInvoicePreviewModal from '@/components/client-po/ClientInvoicePreviewModal';
import BrandSearchDropdown from '@/Components/shared/BrandSearchDropdown';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useClientSession } from '@/lib/ClientSessionContext';
import { toast } from 'sonner';

const createEmptyProduct = () => ({
  nama_product: '',
  qty: 0,
  botol_id: null,
  tutup_id: null,
  spray_id: null,
  racikan: [],
  catatan: '',
  harga_jual_per_pcs: 0,
  total_harga_jual: 0,
  jasa_nominal: 0,
  komponen_tambahan: [],
});

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const getHargaJual = (item) => item?.harga_jual || (item?.harga_rupiah ? item.harga_rupiah * 1.3 : 0);

export default function ClientPOForm() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { createPO } = useClientPOs();
  const [products, setProducts] = useState([createEmptyProduct()]);
  const [legalitas, setLegalitas] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [poMode, setPoMode] = useState('database'); // Hanya Repeat Order untuk Client
  const [isCheckout, setIsCheckout] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isInvoicePreviewOpen, setIsInvoicePreviewOpen] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');

  // Fetch Master Data (Asumsi API sudah tersedia di base44Client)
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: legalitasListFinance = [] } = useQuery({ queryKey: ['legalitas'], queryFn: () => base44.entities.Legalitas.list() });
  const { data: allClientPOs = [] } = useQuery({ queryKey: ['client-pos'], queryFn: () => base44.entities.ClientPO.list() });
  const { data: clientAccounts = [] } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });

  const addProduct = () => setProducts(prev => [...prev, createEmptyProduct()]);
  const updateProduct = (idx, data) => setProducts(prev => prev.map((p, i) => i === idx ? { ...p, ...data } : p));
  const removeProduct = (idx) => setProducts(prev => prev.filter((_, i) => i !== idx));

  const totalHargaJualProduk = products.reduce((sum, p) => {
    const q = Math.max(0, parseInt(p.qty) || 0);
    const unitP = p.custom_harga_jual || p.harga_jual_per_pcs || 0;
    return sum + (q * unitP);
  }, 0);
  const totalHargaLegalitas = legalitas.reduce((sum, l) => sum + (l.harga_jual * (l.qty_variant || 1)), 0);
  const totalHargaJualSemua = totalHargaJualProduk + totalHargaLegalitas;
  const hasValidOrder = products.length > 0 && products.some(p => p.nama_product && (parseInt(p.qty) || 0) > 0);
  
  // Auto-fill Feature dari Database Brand Asli
  const { data: dbBrandsRaw = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list() });
  const { data: dbBahanRaw = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list() });
  const { clientUser } = useClientSession();
  const isDeveloper = 
    clientUser?.role === 'developer' || 
    clientUser?.role === 'admin' || 
    clientUser?.brand_name?.toLowerCase().includes('dev') || 
    clientUser?.email?.toLowerCase().includes('satalawebdeveloper') ||
    clientUser?.email?.toLowerCase().includes('dev');

  const assignedBrands = isDeveloper ? [] : (clientUser?.assigned_brands || (clientUser?.brand_name ? [clientUser.brand_name] : []));

  // Kumpulan seluruh brand unik di database
  const allDbBrands = useMemo(() => {
    const set = new Set();
    dbBrandsRaw.forEach(b => { if (b.nama_brand) set.add(b.nama_brand.trim()); });
    dbBahanRaw.forEach(b => { if (b.nama_brand) set.add(b.nama_brand.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [dbBrandsRaw, dbBahanRaw]);

  // Brand yang diizinkan untuk akun ini (sesuai hak akses / role)
  const accessibleBrands = useMemo(() => {
    if (isDeveloper || clientUser?.role === 'admin') {
      return allDbBrands;
    }
    if (assignedBrands.length > 0) {
      return assignedBrands;
    }
    return allDbBrands;
  }, [isDeveloper, clientUser, assignedBrands, allDbBrands]);

  const [activeBrand, setActiveBrand] = useState(null);
  const [searchBrand, setSearchBrand] = useState('');

  const matchedClient = useMemo(() => {
    if (!activeBrand || !clientAccounts.length) return null;
    const cleanActive = activeBrand.trim().toLowerCase();
    return clientAccounts.find(c => (c.brand_name || '').trim().toLowerCase() === cleanActive) || null;
  }, [activeBrand, clientAccounts]);

  const brandList = React.useMemo(() => {
    if (!activeBrand) return [];
    const map = {};
    const targetBrandLower = activeBrand.trim().toLowerCase();
    
    // Process Racikan
    dbBrandsRaw.forEach(b => {
      if (!b.nama_brand || b.nama_brand.trim().toLowerCase() !== targetBrandLower) return;
      
      let varianOnly = b.nama_varian || b.nama_variant || '';
      let sName = b.ukuran_ml ? String(b.ukuran_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1]; // Use the sub-item name as the unique size key
      } else if (varianOnly.includes(' - Item ')) {
        const parts = varianOnly.split(' - Item ');
        varianOnly = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      const vName = `${b.nama_brand} - ${varianOnly}`;
      if (!map[vName]) map[vName] = { id: vName, nama_product: vName, sizes: {} };
      
      if (!map[vName].sizes[joinKey]) map[vName].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      map[vName].sizes[joinKey].racikan = b;
    });

    // Process Bahan
    dbBahanRaw.forEach(b => {
      if (!b.nama_brand || b.nama_brand.trim().toLowerCase() !== targetBrandLower) return;
      
      let varianOnly = b.nama_varian || b.nama_variant || '';
      let sName = b.botol_ukuran_label_ml ? String(b.botol_ukuran_label_ml) : 'Unknown';
      let joinKey = sName;

      // Logic for Discovery Set grouping
      if (varianOnly.includes(' || ')) {
        const parts = varianOnly.split(' || ');
        varianOnly = parts[0];
        sName = `${parts[1]} (${sName}ml)`;
        joinKey = parts[1];
      } else if (varianOnly.includes(' - Item ')) {
        const parts = varianOnly.split(' - Item ');
        varianOnly = parts[0];
        sName = `Item ${parts[1]} (${sName}ml)`;
        joinKey = `Item ${parts[1]}`;
      }

      const vName = `${b.nama_brand} - ${varianOnly}`;
      if (!map[vName]) map[vName] = { id: vName, nama_product: vName, sizes: {} };
      
      if (!map[vName].sizes[joinKey]) map[vName].sizes[joinKey] = { size: sName, racikan: null, bahan: null };
      map[vName].sizes[joinKey].bahan = b;
    });

    return Object.values(map).map(v => {
      // Sort sizes numerically
      const sizes = Object.values(v.sizes).sort((a, b) => (parseFloat(a.size) || 0) - (parseFloat(b.size) || 0)).map(s => {
        // Build payload for this size
        const b = s.bahan;
        const r = s.racikan;
        
        const matchedBotol = b ? botolList.find(x => (x.nama || '').trim().toLowerCase() === (b.botol_nama || '').trim().toLowerCase()) : null;
        const matchedTutup = b ? tutupList.find(x => (x.nama || '').trim().toLowerCase() === (b.tutup_nama || '').trim().toLowerCase()) : null;
        const matchedSpray = b ? sprayList.find(x => (x.nama || '').trim().toLowerCase() === (b.spray_nama || '').trim().toLowerCase()) : null;
        
        const mappedRacikan = (r?.racikan || []).map(rc => {
          const matchedIng = bahanList.find(
            x => (x.nama || '').trim().toLowerCase() === (rc.nama_bahan || '').trim().toLowerCase()
          );
          const fallbackId = matchedIng ? matchedIng.id : `fallback-${(rc.nama_bahan || '').replace(/\s+/g, '-')}`;
          return {
            kategori: rc.kategori_bahan || 'Bahan',
            bahan_id: fallbackId,
            bahan_nama: rc.nama_bahan,
            persentase: rc.persentase || 0,
            satuan: matchedIng ? (matchedIng.satuan || 'kg') : 'kg',
            harga_per_satuan: matchedIng ? (matchedIng.harga_rupiah || 0) : 0,
            harga_jual_satuan: matchedIng ? (matchedIng.harga_jual || (matchedIng.harga_rupiah * 1.3) || 0) : 0
          };
        });

        let resolvedHargaJual = b?.custom_harga_jual || b?.customHargaJual || 0;
        if (!resolvedHargaJual || Number(resolvedHargaJual) <= 0) {
          const pastPO = allClientPOs.find(cpo => {
            const matchBrand = (cpo.customerName || '').toLowerCase().includes(targetBrandLower);
            return matchBrand && (cpo.products || []).some(p => 
              (p.nama_product || '').toLowerCase().includes(v.nama_product.toLowerCase()) ||
              v.nama_product.toLowerCase().includes((p.nama_product || '').toLowerCase())
            );
          });
          if (pastPO) {
            const pastProd = (pastPO.products || []).find(p => 
              (p.nama_product || '').toLowerCase().includes(v.nama_product.toLowerCase()) ||
              v.nama_product.toLowerCase().includes((p.nama_product || '').toLowerCase())
            );
            if (pastProd) {
              resolvedHargaJual = pastProd.harga_jual_per_pcs || pastProd.final_harga_jual_per_pcs || (pastProd.total_harga_jual ? pastProd.total_harga_jual / (pastProd.qty || 1) : 0);
            }
          }
        }

        return {
          size: s.size,
          bahan: b,
          custom_harga_jual: resolvedHargaJual || 0,
          custom_margin: b?.custom_margin || 0,
          custom_jasa: b?.custom_jasa,
          botol_id: matchedBotol ? matchedBotol.id : null,
          tutup_id: matchedTutup ? matchedTutup.id : null,
          spray_id: matchedSpray ? matchedSpray.id : null,
          botol_dari_client: matchedBotol ? false : true,
          botol_ukuran_client: parseFloat(s.size) || 0,
          racikan: mappedRacikan,
          komponen_tambahan: (b?.item_tambahan || []).map(it => ({
            nama: it.nama,
            kategori: it.kategori,
            harga: it.harga || 0,
            harga_jual: it.harga_jual || 0
          }))
        };
      });

      return {
        ...v,
        sizes
      };
    });
  }, [dbBrandsRaw, dbBahanRaw, activeBrand, botolList, tutupList, sprayList, bahanList, allClientPOs]);

  // Validasi: Pastikan nama terisi dan racikan 100%
  const canSave = products.every(p => {
    const totalPersen = (p.racikan || []).reduce((s, r) => s + (r.persentase || 0), 0);
    const hasBotol = p.botol_id || (p.botol_dari_client && p.botol_ukuran_client > 0);
    return p.nama_product.trim() && Math.abs(totalPersen - 100) < 0.01 && hasBotol;
  });

  const globalSlaHari = React.useMemo(() => {
    let maxSla = 2;
    products.forEach(product => {
      let slaHari = 2;
      const q = product.qty || 1;
      if (q > 2000) slaHari = 7;
      else if (q >= 1000) slaHari = 5;
      else if (q >= 500) slaHari = 3;

      const adaBox = (product.komponen_tambahan || []).some(k => 
        k.nama?.toLowerCase().includes('box') || k.kategori?.toLowerCase().includes('box')
      );
      if (adaBox) slaHari = 15;
      
      if (slaHari > maxSla) maxSla = slaHari;
    });
    return maxSla;
  }, [products]);

  const handleSubmit = () => {
    if (!canSave) return;
    setIsCheckout(true);
  };

  const [createdPOData, setCreatedPOData] = useState(null);

  const handleCompletePayment = async (checkoutData) => {
    const { 
      paymentTerms = 'dp', 
      dpPercentage = 50, 
      tempoDays = null,
      tanggal_jatuh_tempo = null,
      rekening_tujuan = null,
      nominalBayarSekarang = 0,
      selectedCourier = 'JNE REG', 
      shippingCost = 0, 
      deliveryMethod = 'kurir',
      customerAddress = '',
      customerName = ''
    } = checkoutData;

    const randomSuffix = Math.floor(100 + Math.random() * 900);
    const dateStr = format(new Date(), 'yyyyMMdd');
    const newPoId = `PO-${dateStr}-${randomSuffix}`;

    const totalJualProd = products.reduce((s, p) => s + (p.total_harga_jual || 0), 0);
    const totalJualLeg = legalitas.reduce((s, l) => s + (l.harga_jual * (l.qty_variant || 1)), 0);
    const totalJualSemua = totalJualProd + totalJualLeg + (shippingCost || 0);

    const isTempo = paymentTerms === 'tempo';
    const isCash = paymentTerms === 'cash';

    let statusBayar = 'MENUNGGU KONFIRMASI (DP 50%)';
    if (isTempo) {
      statusBayar = `TEMPO (${tempoDays} HARI)`;
    } else {
      statusBayar = 'MENUNGGU KONFIRMASI (DP 50%)';
    }

    const statusProduksi = 'BELUM DIKONFIRMASI';

    const riwayatPembayaran = [
      {
        jumlah: isTempo ? 0 : nominalBayarSekarang,
        tanggal_bayar: format(new Date(), 'dd MMM yyyy'),
        catatan: isTempo 
          ? `Termin Tempo ${tempoDays} Hari (Jatuh Tempo: ${tanggal_jatuh_tempo ? format(new Date(tanggal_jatuh_tempo), 'dd MMM yyyy') : '-'}) via ${rekening_tujuan?.bankName || 'BCA Satala'}`
          : `Pembayaran DP 50% via ${rekening_tujuan?.bankName || 'BCA Satala'} (Sisa 50% dilunasi saat barang siap kirim)`
      }
    ];

    // Set deadline dynamically (e.g. 20 days by default)
    const deadlineDate = new Date();
    deadlineDate.setDate(deadlineDate.getDate() + 20);
    const deadlineStr = format(deadlineDate, 'dd MMM yyyy');

    const brandNameToSave = activeBrand || clientUser?.brand_name || 'Brand Satala';

    const newPO = {
      id: newPoId,
      clientId: clientUser?.id || 'cli_default',
      brand_name: brandNameToSave,
      client: brandNameToSave,
      customerEmail: clientUser?.email || '',
      varianLabel: products.map(p => `${p.nama_product || 'Varian Kustom'} (${p.qty} pcs)`).join(', '),
      date: format(new Date(), 'dd MMM yyyy'),
      created_date: new Date().toISOString(),
      statusProduksi, 
      statusBayar,
      customerName: customerName || (clientUser?.company_name ? `${clientUser.company_name} / ${clientUser.pic || clientUser.brand_name}` : 'PT Client Satala'),
      customerAddress: customerAddress || 'Alamat Penerima',
      phone: clientUser?.phone || '08xxxxxxxx',
      courier: deliveryMethod === 'kurir' ? selectedCourier : 'Ambil Sendiri',
      resi: deliveryMethod === 'kurir' ? 'MENUNGGU PICKUP' : '-',
      shippingCost: shippingCost,
      dpPercentage: isTempo ? 0 : 50,
      paymentTerms: paymentTerms,
      tempoDays: tempoDays,
      tanggal_jatuh_tempo: tanggal_jatuh_tempo,
      rekening_tujuan: rekening_tujuan,
      paymentMethod: rekening_tujuan?.key || 'bca',
      grand_total: totalJualSemua,
      total_amount: totalJualSemua,
      deadline: deadlineStr,
      items: products.map(p => ({
        nama: p.nama_product || 'Varian Kustom',
        nama_product: p.nama_product || 'Varian Kustom',
        qty: p.qty,
        selected_size: p.selected_size,
        hargaTotal: p.total_harga_jual || 0,
        total_harga_jual: p.total_harga_jual || 0,
        harga_jual_per_pcs: p.harga_jual_per_pcs || 0,
        hasImage: false
      })),
      products: products.map(p => {
        const totalJual = p.total_harga_jual || 0;
        const totalHpp = Math.round(totalJual * 0.65);
        return {
          nama_product: p.nama_product || 'Varian Kustom',
          qty: p.qty,
          botol_id: p.botol_id,
          tutup_id: p.tutup_id,
          spray_id: p.spray_id,
          botol_dari_client: p.botol_dari_client,
          botol_ukuran_client: p.botol_ukuran_client,
          botol_nama: p.botol_id ? botolList.find(b => b.id === p.botol_id)?.nama : 'Botol Standar',
          tutup_nama: p.tutup_id ? tutupList.find(t => t.id === p.tutup_id)?.nama : null,
          spray_nama: p.spray_id ? sprayList.find(s => s.id === p.spray_id)?.nama : null,
          racikan: p.racikan || [],
          komponen_tambahan: p.komponen_tambahan || [],
          jasa_nominal: p.jasa_nominal || 4000,
          harga_jual_per_pcs: p.harga_jual_per_pcs,
          total_harga_jual: totalJual,
          total_hpp_product: totalHpp,
          hpp_per_pcs_digunakan: Math.round(totalHpp / p.qty)
        };
      }),
      legalitas: legalitas.map(l => ({
        nama: l.nama,
        harga_jual: l.harga_jual,
        qty_variant: l.qty_variant || 1
      })),
      timeline: [
        { status: 'Pesanan Dibuat', date: format(new Date(), 'dd MMM yyyy HH:mm'), desc: 'Pesanan Dibuat', done: true, active: true },
        { status: 'Dikonfirmasi Tim Satala', date: isTempo ? '-' : format(new Date(), 'dd MMM yyyy'), desc: isTempo ? 'Termin Tempo Disetujui' : 'Menunggu Verifikasi DP 50% oleh Tim Satala', done: false },
        { status: 'Sedang PO Item', date: '-', desc: 'Pengadaan Bahan & Kemasan', done: false },
        { status: 'Antrean Produksi', date: '-', desc: 'Bahan & Kemasan Siap', done: false },
        { status: 'Proses Produksi', date: '-', desc: '-', done: false },
        { status: 'Pesanan Selesai', date: '-', desc: '-', done: false }
      ],
      riwayatPembayaran,
      resiTimeline: []
    };

    // Save to Supabase via hook
    await createPO(newPO);

    setCreatedPOData({
      id: newPoId,
      brand_name: brandNameToSave,
      paymentTerms,
      dpPercentage: isTempo ? 0 : 50,
      tempoDays,
      tanggal_jatuh_tempo,
      totalJualSemua,
      nominalBayarSekarang: isTempo ? 0 : nominalBayarSekarang,
      sisaPelunasan: isTempo ? totalJualSemua : (totalJualSemua - nominalBayarSekarang),
      rekening_tujuan
    });

    setSubmitted(true);
  };

  const closeSuccessModal = () => {
    setProducts([createEmptyProduct()]); 
    setSubmitted(false); 
    setIsCheckout(false);
    navigate('/daftar-pesanan');
  };

  if (submitted && createdPOData) {
    const isTempo = createdPOData.paymentTerms === 'tempo';
    const jatuhTempoTgl = createdPOData.tanggal_jatuh_tempo 
      ? format(new Date(createdPOData.tanggal_jatuh_tempo), 'dd MMMM yyyy')
      : '-';

    return (
      <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in">
        <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col border border-[#E6D5B8] animate-in zoom-in-95 duration-300">
          {/* Header ASA */}
          <div className="bg-gradient-to-br from-[#F9F5EC] via-white to-[#F9F5EC] p-6 text-center relative border-b border-[#E6D5B8]">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#8C6208] rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg shadow-[#D4AF37]/25 text-white">
              <Bot className="w-8 h-8" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#8C6208] bg-white border border-[#E6D5B8] px-3 py-1 rounded-full shadow-xs">
              Asisten Satala (ASA)
            </span>
            <h2 className="text-xl font-black text-[#302621] mt-2 tracking-tight">Terima Kasih Sudah PO di Satala! ✨</h2>
            <p className="text-xs text-[#7A695B]">Pesanan Repeat Order Anda telah berhasil dicatat ke sistem kami.</p>
          </div>

          <div className="p-6 space-y-4 text-xs">
            {/* Info Box PO */}
            <div className="bg-[#FFFDF9] border border-[#E6D5B8] rounded-2xl p-4 divide-y divide-slate-100">
              <div className="flex justify-between items-center pb-2.5">
                <span className="text-[#7A695B] font-medium">Nomor PO:</span>
                <span className="font-mono font-black text-sm text-[#8C6208]">{createdPOData.id}</span>
              </div>
              <div className="flex justify-between items-center py-2.5">
                <span className="text-[#7A695B] font-medium">Brand:</span>
                <span className="font-bold text-[#302621]">{createdPOData.brand_name}</span>
              </div>
              <div className="flex justify-between items-center py-2.5">
                <span className="text-[#7A695B] font-medium">Termin Pembayaran:</span>
                <span className="font-black text-[#8C6208] uppercase">
                  {isTempo ? `Tempo ${createdPOData.tempoDays} Hari` : 'Cash (DP 50% + Pelunasan 50%)'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5">
                <span className="text-[#7A695B] font-medium">Total Nilai Pesanan:</span>
                <span className="font-black text-sm text-[#302621]">Rp {fmt(createdPOData.totalJualSemua)}</span>
              </div>

              {!isTempo ? (
                <>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-[#7A695B] font-medium">DP 50% (Sudah Ditransfer):</span>
                    <span className="font-black text-emerald-700">Rp {fmt(createdPOData.nominalBayarSekarang)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2.5">
                    <span className="text-[#7A695B] font-medium">Sisa Pelunasan (Saat Siap Kirim):</span>
                    <span className="font-bold text-[#8C6208]">Rp {fmt(createdPOData.sisaPelunasan)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-blue-700 font-bold">Ketentuan Jatuh Tempo:</span>
                    <span className="font-black text-blue-900">
                      {createdPOData.tempoDays || 30} Hari ({Number(createdPOData.tempoDays || 30) === 30 ? '1 Bulan' : `${createdPOData.tempoDays} Hari`})
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-[#7A695B] font-medium">Mulai Terhitung:</span>
                    <span className="font-bold text-[#8C6208]">Setelah Barang Datang / PO Selesai</span>
                  </div>
                  <div className="flex justify-between items-center pt-2.5">
                    <span className="text-[#7A695B] font-medium">Rekening Pembayaran Nanti:</span>
                    <span className="font-bold text-[#302621]">
                      {createdPOData.rekening_tujuan?.accountName 
                        ? `${createdPOData.rekening_tujuan.bankName} - ${createdPOData.rekening_tujuan.accountName}` 
                        : (createdPOData.rekening_tujuan?.bankName || 'BCA')} ({createdPOData.rekening_tujuan?.accountNumber || '6821535384'})
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Pesan ASA yang Hangat & Sesuai Pilihan Cash / Tempo */}
            <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-2xl p-4 relative">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#8C6208]" />
                <span className="font-black text-[#302621]">Pesan dari ASA untuk Kakak:</span>
              </div>

              {!isTempo ? (
                <div className="text-[#302621] space-y-2 leading-relaxed">
                  <p className="italic">
                    "Halo Kak! Terima kasih banyak atas Repeat Order-nya untuk brand <strong>{createdPOData.brand_name}</strong> di PT Satala Dermatech Essential ✨
                  </p>
                  <p>
                    Pembayaran <strong>DP 50%</strong> sebesar <strong>Rp {fmt(createdPOData.nominalBayarSekarang)}</strong> telah kami catat dan akan segera dikonfirmasi oleh <strong>Tim Satala</strong> kami. Setelah itu, pesanan langsung masuk ke tahapan <strong>Sedang PO Item</strong> atau <strong>Antrean Produksi</strong>.
                  </p>
                  <p className="text-[#8C6208] font-bold">
                    Untuk sisa pelunasan 50% (Rp {fmt(createdPOData.sisaPelunasan)}), dapat diselesaikan nanti ketika seluruh produk sudah siap kirim ya Kak. Semoga batch produksi ini lancar dan produknya makin sukses di pasaran! 🚀💖"
                  </p>
                </div>
              ) : (
                <div className="text-[#302621] space-y-2 leading-relaxed">
                  <p className="italic">
                    "Halo Kak! Terima kasih banyak atas Repeat Order-nya untuk brand <strong>{createdPOData.brand_name}</strong> di PT Satala Dermatech Essential ✨
                  </p>
                  <p>
                    Pesanan Kakak dengan fasilitas termin <strong>Tempo {createdPOData.tempoDays || 30} Hari ({Number(createdPOData.tempoDays || 30) === 30 ? '1 Bulan' : `${createdPOData.tempoDays} Hari`})</strong> telah kami catat. <strong>Tim Satala</strong> kami akan langsung memproses pengadaan item dan memasukkan pesanan Kakak ke antrean produksi pabrik.
                  </p>
                  <p className="text-blue-900 font-bold bg-blue-50/80 p-2.5 rounded-xl border border-blue-200/80">
                    💡 Pengingat ASA: Tagihan sebesar <strong>Rp {fmt(createdPOData.totalJualSemua)}</strong> akan jatuh tempo <strong>{createdPOData.tempoDays || 30} hari ({Number(createdPOData.tempoDays || 30) === 30 ? '1 bulan' : `${createdPOData.tempoDays} hari`}) setelah barang pesanan selesai diproduksi & tiba di alamat Kakak</strong>. Pembayaran pelunasan nantinya dapat ditransfer ke rekening {createdPOData.rekening_tujuan?.accountName ? `${createdPOData.rekening_tujuan.bankName} - ${createdPOData.rekening_tujuan.accountName}` : (createdPOData.rekening_tujuan?.bankName || 'BCA')} ({createdPOData.rekening_tujuan?.accountNumber || '6821535384'}) ya Kak. Sukses selalu untuk brand Kakak! 🚀💖"
                  </p>
                </div>
              )}
            </div>

            {/* Button ke Daftar Pesanan */}
            <Button 
              onClick={closeSuccessModal} 
              className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
            >
              <span>Lihat Progres di Daftar Pesanan</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isCheckout) {
    return (
      <ClientPOCheckout 
        products={products}
        legalitas={legalitas}
        botolList={botolList}
        tutupList={tutupList}
        sprayList={sprayList}
        itemTambahanList={itemTambahanList}
        totalHargaJualSemua={totalHargaJualSemua}
        brandName={activeBrand}
        activeBrand={activeBrand}
        matchedClient={matchedClient}
        onBack={() => setIsCheckout(false)}
        onComplete={handleCompletePayment}
      />
    );
  }

  // Jika belum memilih brand, tampilkan layar "Cari Brand Klien" persis seperti di gambar
  if (!activeBrand) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-sm border border-[#E6D5B8]">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#F9F5EC] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#E6D5B8]">
              <Search className="w-7 h-7 text-[#8C6208]" />
            </div>
            <h2 className="text-3xl font-black text-[#302621] mb-3 tracking-tight">Cari Brand Klien</h2>
            <p className="text-[#7A695B] mb-8 text-sm leading-relaxed">
              Pilih brand klien yang akan dibuatkan pesanan Repeat Order. Seluruh varian yang telah tersimpan di Database akan otomatis ditarik.
            </p>
          </div>
          
          <BrandSearchDropdown
            brands={accessibleBrands}
            value={searchBrand}
            onChange={setSearchBrand}
            onSelect={(brand) => {
              if (brand && brand.trim()) {
                const b = brand.trim();
                setSearchBrand(b);
                setActiveBrand(b);
                setProducts([createEmptyProduct()]);
                toast.success(`Brand "${b}" dipilih. Memuat katalog varian...`);
              }
            }}
            placeholder="Ketik nama brand..."
          />

          <div className="mt-6">
            <Button 
              onClick={() => {
                if (searchBrand.trim()) {
                  const b = searchBrand.trim();
                  setActiveBrand(b);
                  setProducts([createEmptyProduct()]);
                  toast.success(`Brand "${b}" dipilih. Memuat katalog varian...`);
                }
              }}
              disabled={!searchBrand.trim()}
              className="w-full bg-[#8C6208] hover:bg-[#6e4c06] text-white py-6 rounded-xl text-base md:text-lg font-bold shadow-lg shadow-[#8C6208]/20 cursor-pointer disabled:opacity-50 transition-all"
            >
              Lanjutkan Buat Pesanan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-32 md:pb-20">
      {/* Top Header & Toolbar */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#E6D5B8] pb-5 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black tracking-widest text-[#8C6208] uppercase bg-[#F9F5EC] px-2.5 py-0.5 rounded-full border border-[#E6D5B8]">
              Portal Repeat Order
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-[#302621] tracking-tight">Buat Pesanan (PO)</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Pilih varian katalog brand Anda, tentukan kuantitas, dan dapatkan kalkulasi harga otomatis.</p>
        </div>
      </div>

      {/* Card Informasi Invoice (Sesuai Screenshot) */}
      <div className="bg-white border border-[#E6D5B8] rounded-xl md:rounded-2xl p-4 md:p-6 mb-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-[#E6D5B8]/60">
          <h3 className="text-sm md:text-base font-bold text-[#302621] flex items-center gap-2">
            <Package2 className="w-4 h-4 text-[#8C6208]" />
            Informasi Invoice
          </h3>
          <div className="flex items-center gap-2">
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setIsImportModalOpen(true)}
              className="h-8 border-[#8C6208]/30 bg-[#8C6208]/5 text-[#8C6208] hover:bg-[#8C6208]/10 rounded-lg text-xs font-bold px-3 flex items-center gap-1.5 shadow-2xs cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#8C6208]" />
              Import PO (AI / Spreadsheet)
            </Button>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => {
                setActiveBrand(null);
                setSearchBrand('');
                setProducts([createEmptyProduct()]);
              }}
              className="h-8 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg text-xs font-bold px-3 cursor-pointer"
            >
              Ganti Brand
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Row 1: Left: Nama Brand */}
          <div>
            <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">
              Nama Brand / Client <span className="text-red-500">*</span>
            </label>
            <div className="h-9 md:h-10 px-3 md:px-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
              <span className="text-[11px] md:text-sm font-bold text-[#302621] truncate pr-2">
                {activeBrand}
              </span>
              <span className="text-[10px] font-bold text-[#8C6208] bg-[#F9F5EC] border border-[#E6D5B8] px-2 py-0.5 rounded-full shrink-0">
                Brand Terpilih
              </span>
            </div>
          </div>

          {/* Row 1: Right: Tanggal Pembuatan Invoice */}
          <div>
            <label className="text-[10px] md:text-xs font-bold text-[#7A695B] mb-1.5 block">
              Tanggal Pembuatan Invoice
            </label>
            <div className="h-9 md:h-10 px-3 md:px-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="text-[11px] md:text-sm font-semibold text-[#302621]">
                {format(new Date(), 'd MMM yyyy')}
              </span>
            </div>
          </div>

          {/* Row 2: Left: PIC (Penanggung Jawab) */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] md:text-xs font-bold text-[#7A695B] flex items-center gap-1">
                PIC (Penanggung Jawab)
              </label>
              <span className="inline-flex items-center justify-center w-5 h-5 text-amber-800 bg-amber-100/70 border border-amber-200 rounded-md shadow-2xs">
                <Lock className="w-3 h-3" />
              </span>
            </div>
            <div className="h-9 md:h-10 px-3 md:px-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
              <span className="text-[11px] md:text-sm font-semibold text-[#302621]">
                {matchedClient?.pic_name || matchedClient?.owner_name || 'Tim Satala'}
              </span>
              <User className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </div>

          {/* Row 2: Right: Termin Pembayaran */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] md:text-xs font-bold text-[#7A695B] flex items-center gap-1">
                Termin Pembayaran
              </label>
              <span className="inline-flex items-center justify-center w-5 h-5 text-amber-800 bg-amber-100/70 border border-amber-200 rounded-md shadow-2xs">
                <Lock className="w-3 h-3" />
              </span>
            </div>
            <div className="h-9 md:h-10 px-3 md:px-4 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
              <span className="text-[11px] md:text-sm font-semibold text-[#302621]">
                {(() => {
                  const terms = matchedClient?.allowed_payment_terms || [];
                  const tempoDays = matchedClient?.tempo_duration_days || 30;
                  const isTempo = terms.includes('tempo') || matchedClient?.is_tempo;
                  const isCash = terms.includes('cash') || terms.includes('dp');
                  
                  if (isTempo && !isCash) {
                    return `Tempo (Jatuh Tempo ${tempoDays} Hari)`;
                  }
                  if (isCash && !isTempo) {
                    return 'Cash (DP 50% & Pelunasan 50%)';
                  }
                  if (isTempo && isCash) {
                    return `Cash (DP 50%) / Tempo ${tempoDays} Hari`;
                  }
                  return 'Cash (DP 50% & Pelunasan 50%)';
                })()}
              </span>
              <CreditCard className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Daftar Produk Varian (Full Width) */}
      <div className="space-y-4">
        {products.map((product, idx) => (
          <ClientProductCard
            key={idx}
            product={product}
            index={idx}
            botolList={botolList}
            tutupList={tutupList}
            sprayList={sprayList}
            bahanList={bahanList}
            itemTambahanList={itemTambahanList}
            isLocked={poMode === 'database'}
            varianDatabase={brandList}
            onChange={(data) => updateProduct(idx, data)}
            onRemove={() => removeProduct(idx)}
            canRemove={products.length > 1}
          />
        ))}

        <button 
          type="button"
          onClick={addProduct} 
          className="w-full border-2 border-dashed border-[#D4AF37] hover:border-[#8C6208] py-4 px-6 text-[#8C6208] hover:text-[#302621] bg-[#FFFDF9] hover:bg-[#F4EDE1] flex items-center justify-center gap-3 rounded-2xl font-bold text-sm shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="w-6 h-6 rounded-full bg-[#E6D5B8]/60 text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white flex items-center justify-center transition-colors">
            <Plus className="w-4 h-4" />
          </div>
          <span>Tambah Varian Produk Lain</span>
        </button>
      </div>

      {/* Ringkasan Tagihan Invoice (Sesuai Gambar 2) */}
      <div className="bg-gradient-to-br from-[#302621] to-[#1A1411] text-[#FFFDF9] rounded-2xl p-5 md:p-6 shadow-xl mb-6 mt-8 border border-white/10">
        <h3 className="text-xs md:text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-4 border-b border-white/10 pb-3">
          Ringkasan Tagihan Invoice
        </h3>
        
        <div className="space-y-3">
          {products.map((p, i) => {
            const qty = parseInt(p.qty) || 0;
            const unitP = p.custom_harga_jual || p.harga_jual_per_pcs || 0;
            const subtotal = p.total_harga_jual || (qty * unitP);
            const size = p.selected_size;

            return (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center text-xs font-bold text-[#302621] shrink-0">
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate text-white">
                      {p.nama_product || `Produk ${i + 1}`}
                    </p>
                    {size > 0 ? (
                      <p className="text-[11px] text-white/70 mt-0.5 font-medium">
                        {size} ml
                      </p>
                    ) : (
                      <p className="text-[11px] text-amber-200/60 mt-0.5 font-normal">
                        {!p.nama_product ? 'Varian belum dipilih' : ''}
                      </p>
                    )}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-[11px] text-white/60">
                    Harga: Rp {fmt(unitP)} × {qty}
                  </p>
                  <p className="font-bold text-white text-base">
                    Rp {fmt(subtotal)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Grand Total */}
        <div className="border-t border-white/20 mt-4 pt-4 flex justify-between items-center">
          <span className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider">
            Grand Total Invoice
          </span>
          <span className="text-2xl sm:text-3xl font-black text-white">
            Rp {fmt(totalHargaJualSemua)}
          </span>
        </div>
      </div>

      {/* Action Buttons (Pratinjau Invoice & Lanjut ke Pembayaran) */}
      <div className="border border-[#E6D5B8] bg-white rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3 mb-12">
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsInvoicePreviewOpen(true)}
          disabled={!hasValidOrder}
          className="w-full sm:w-auto h-12 px-6 border-[#8C6208] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-xs disabled:opacity-40"
        >
          <Printer className="w-4 h-4" />
          <span>Pratinjau Invoice</span>
        </Button>

        <Button
          type="button"
          onClick={() => setIsCheckout(true)}
          disabled={!hasValidOrder}
          className="w-full sm:flex-1 max-w-md h-12 bg-[#302621] hover:bg-[#1A1411] text-white font-black rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg shadow-[#302621]/20 cursor-pointer disabled:opacity-40 transition-all"
        >
          <span>Lanjut ke Pembayaran</span>
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Floating Bar Khusus Mobile (lg:hidden) */}
      <div className="lg:hidden fixed bottom-[62px] left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#E6D5B8] p-3.5 shadow-[0_-10px_30px_rgba(0,0,0,0.06)] z-40">
        <div className="container mx-auto flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider mb-0.5 truncate">Total Tagihan</p>
            <p className="text-lg font-black text-[#302621] truncate">Rp {fmt(totalHargaJualSemua)}</p>
          </div>
          <Button
            onClick={() => setIsCheckout(true)}
            disabled={!hasValidOrder}
            className="bg-[#302621] text-white hover:bg-[#1A1411] h-11 px-5 rounded-xl font-bold text-xs gap-1.5 shadow-sm"
          >
            <span>Lanjut Bayar</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Modals */}
      <ClientPOImportModal 
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        activeBrand={activeBrand}
        brandCatalog={brandList}
        onApplyItems={(newItems) => setProducts(newItems)}
      />

      <ClientInvoicePreviewModal 
        isOpen={isInvoicePreviewOpen}
        onClose={() => setIsInvoicePreviewOpen(false)}
        products={products}
        legalitas={legalitas}
        activeBrand={activeBrand}
        clientUser={clientUser}
        matchedClient={matchedClient}
        totalHargaJualSemua={totalHargaJualSemua}
        onProceedToCheckout={handleSubmit}
      />
    </div>
  );
}
